import { set } from 'vue'
import { defineStore } from 'pinia'

import { PaginatedResponse } from '@/common/models/data/common/pagination/paginated-response'
import { Post } from '@/common/models/data/dashboard/posts/post'

import PostsRepository from '@/app/repositories/posts-repository'

const usePostsStore = defineStore('posts', {
    state: () => ({
        data: new PaginatedResponse<Post>(),
        dates: [] as Array<string>,
        publishedAt: {} as Record<string, PaginatedResponse<Post>>,
        userEntries: {} as Record<string, PaginatedResponse<Post>>,
        tagged: {} as Record<string, PaginatedResponse<Post>>,
        loading: false
    }),
    actions: {
        async load (page = 1): Promise<void> {
            this.updateDataFrom(await PostsRepository.index(page))
        },
        async loadNext (): Promise<void> {
            this.loading = true

            const posts = await PostsRepository.index(this.data.meta.nextPage)

            this.loading = false
            this.updateDataFrom(posts)
        },
        async loadByAuthor (payload: { authorId: number, page: number }): Promise<void> {
            this.setByUser({
                userId: payload.authorId,
                data: await PostsRepository.byAuthor(payload.authorId, payload.page)
            })
        },
        async loadByDate (payload: { date: Date, page: number }): Promise<void> {
            this.setByDate({
                date: payload.date,
                data: await PostsRepository.byDate(payload.date, payload.page)
            })
        },
        async loadByTag (payload: { tagSlug: string, page: number }): Promise<void> {
            this.setByTag({
                tag: payload.tagSlug,
                data: await PostsRepository.byTag(payload.tagSlug, payload.page)
            })
        },
        async loadDates (): Promise<void> {
            this.dates = await PostsRepository.dates()
        },
        setByDate (payload: { date: Date, data: PaginatedResponse<Post> }): void {
            set(this.publishedAt, payload.date.toISOString(), payload.data)
        },
        setByUser (payload: { userId: number, data: PaginatedResponse<Post> }): void {
            set(this.userEntries, payload.userId.toString(), payload.data)
        },
        setByTag (payload: { tag: string, data: PaginatedResponse<Post> }): void {
            set(this.tagged, payload.tag, payload.data)
        },
        updateDataFrom (payload: PaginatedResponse<Post>): void {
            if (payload.meta.current_page === 1) {
                this.data.copyFrom(payload)
            } else {
                this.data.mergeWith(payload)
            }
        }
    },
    getters: {
        byDate: (state) => {
            return (date: Date): PaginatedResponse<Post> => {
                return state.publishedAt[date.toISOString()] ?? new PaginatedResponse()
            }
        },
        byAuthor: (state) => {
            return (authorId: number): PaginatedResponse<Post> => {
                return state.userEntries[authorId] ?? new PaginatedResponse()
            }
        },
        byTag: (state) => {
            return (slug: string): PaginatedResponse<Post> => {
                return state.tagged[slug] ?? new PaginatedResponse()
            }
        }
    }
})

export {
    usePostsStore
}
